import { ReactNode, useEffect, useState } from 'react'
import { ReactFC } from 'common/types/ReactFC'
import HeadingWithSpan from 'components/HeadingWithSpan'
import { konnectStrings } from 'common/strings/konnect-strings'
import dataRelay from 'data-relay'
import { Project } from 'common/project'
import { sectionIndent } from 'project/padding'
import runWhenIdle from 'common/runWhenIdle'
import { useNearViewport } from 'common/useNearViewport'

interface EmbededKonnectFormType {
  id?: string
  au?: boolean
  title?: string
}

// Add HubSpot types
declare global {
  interface Window {
    hbspt: {
      forms: {
        create: (config: {
          formId: string
          portalId: string
          target: string
          onFormReady?: (form: any) => void
          onFormSubmit?: (
            form: any,
            formDataArray: Array<{ name: string; value: string }>,
          ) => void
        }) => void
      }
    }
  }
}

const customStyle = `
.hs-custom-style {
  font-family: "Open Sans", sans-serif !important;
}
.hs-richtext {
  font-family: "Open Sans", sans-serif !important;
}
label {
  font-size: 15px !important;
  font-weight: 600 !important;
  font-family: "Open Sans", sans-serif !important;
}
.actions {
  display: flex;
  justify-content: flex-end;
}
@media (max-width: 500px) {
  .actions {
    display: block;
    .hs-button {
    width: 100%;
    }
  }
}
input[type="submit"] {
  background-color: #b82aa9 !important;
  height: 44px;
  border-radius: 22px;
  border-color: #b82aa9 !important;
  padding: 10px 104px;
  font-size: 14px !important;
  font-weight: 400;
}
.input input, .input textarea {
background-color: transparent !important;
border: 1px solid #8B8B8F;
border-radius: 8px;
}
.hs-error-msgs label {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: ##cb0909 !important;
}
.input.hs-input.error {
  border-color: #cb0909 !important;
}
.submitted-message {
  p {
    font-size: 24px !important;
    font-weight: 700 !important;
    span {
      font-weight: 400 !important;
      font-size: 16px !important;
    }
  }
}
`
const formId = 'konnect-form'

const EmbededKonnectForm: ReactFC<EmbededKonnectFormType> = ({
  au,
  id = 'konnect-form-container',
  title = 'Get in touch to find out more!',
}) => {
  const [submitted, setSubmitted] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(formId)) {
      return setSubmitted(true)
    }
  }, [])

  const [ref] = useNearViewport({
    onEnter: () => {
      if (window.hbspt?.forms) {
        setIsScriptLoaded(true)
        return
      }

      const src = '//js.hsforms.net/forms/embed/v2.js'
      let script = document.querySelector<HTMLScriptElement>(
        `script[src="${src}"]`,
      )

      if (script) {
        if (!window.hbspt?.forms) {
          script.addEventListener('load', () => setIsScriptLoaded(true), {
            once: true,
          })
        }
        return
      }

      script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = () => {
        setIsScriptLoaded(true)
      }
      document.head.appendChild(script)
    },
  })

  useEffect(() => {
    if (!isScriptLoaded || !window.hbspt?.forms) return

    window.hbspt.forms.create({
      formId: au
        ? '74a899ec-7907-4a3d-bcb4-41ea7f26ea29'
        : 'b753e8dc-a398-42f5-b08a-144f7d4b8536',
      onFormReady: function () {
        const formIframe = document.querySelector(
          `#${id} iframe`,
        ) as HTMLIFrameElement
        if (formIframe) {
          const iframeDoc =
            formIframe.contentDocument || formIframe.contentWindow?.document
          if (iframeDoc) {
            const style = iframeDoc.createElement('style')
            style.textContent = customStyle
            iframeDoc.head.appendChild(style)
          }
        }
      },
      onFormSubmit: function (
        form: any,
        formDataArray: Array<{ name: string; value: string }>,
      ) {
        // Create a result object
        const result: Record<string, any> = {}
        const ignoredKeys = [
          'hs_context',
          '0-2/name',
          'LEGAL_CONSENT.subscription_type_458830746',
        ]
        localStorage.setItem(formId, 'true')

        // Iterate through the array
        formDataArray.forEach(({ name, value }) => {
          if (!ignoredKeys.includes(name)) {
            result[name] = value
          }
        })

        if (!Project.dataRelayKonnect) {
          return
        }
        dataRelay.sendEvent(
          {
            source: document.location.pathname,
            ...result,
          },
          { apiKey: Project.dataRelayKonnect },
        )
      },
      portalId: '48114419',
      target: `#${id}`,
    })
  }, [isScriptLoaded, id])

  return (
    <div ref={ref} className={sectionIndent} id={'konnect-form'}>
      <HeadingWithSpan
        size={3}
        className={
          'fs-fluid-36-64 text-semibold mx-20 mx-lg-0 lh-105 text-center mb-lg-1'
        }
      >
        {title}
      </HeadingWithSpan>
      <p
        className={'fs-fluid-18-36 mb-6 text-semibold text-muted text-center'}
        dangerouslySetInnerHTML={{
          __html: konnectStrings.getInTouchToFindOutMoreSubtitle,
        }}
      />
      <div id={id} className={sectionIndent}>
        {!isScriptLoaded && (
          <div className='text-center'>
            <p>Loading form...</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmbededKonnectForm
